/**
 * Блок «Население» районной вкладки (US-11 FR-3).
 *
 * Общая численность на первое число месяца + городская и сельская части,
 * каждая — в людях и в процентах от общей. Доля считается на экране как
 * «категория ÷ итог × 100», один знак после запятой (FR-3.2).
 *
 * Итог и разбивка приходят из одного источника — методология БНС
 * (FR-3.4, решение 1 от 03.09). Разбивка из «Мониторинга СНП» здесь не
 * используется: у неё к городскому населению отнесён только сам город,
 * и по району Магжана Жумабаева вместо 33,3 % получалось бы 2,3 %.
 *
 * Разбивки нет → показывается только общая численность, без нулей по
 * категориям (FR-3.5). Нет и итога → «нет данных» по блоку.
 */
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';

interface Props {
  /** Общая численность, человек. */
  total: number | null;
  /** Городское население, человек; null — разбивки нет (FR-3.5). */
  urban: number | null;
  /** Сельское население, человек; null — разбивки нет (FR-3.5). */
  rural: number | null;
  /** Отчётная дата блока («на 1 июля 2026 года»), своя (FR-3.3, FR-7.2). */
  periodLabel?: string | null;
}

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);
const fmtPct = (v: number): string =>
  `${Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(v)} %`;

export const PopulationSplit = ({
  total,
  urban,
  rural,
  periodLabel,
}: Props) => {
  const { t } = useTranslation();

  const share = (value: number): string | null =>
    total !== null && total !== 0 ? fmtPct((value / total) * 100) : null;

  const parts = (
    [
      ['urban', urban],
      ['rural', rural],
    ] as const
  ).flatMap(([key, value]) =>
    value === null ? [] : [{ key, value, share: share(value) }],
  );

  return (
    <Card className="shadow-card">
      <CardHeader className="flex-row items-baseline justify-between space-y-0">
        <CardTitle>{t('digitalPassport.districtPopulation.title')}</CardTitle>
        {periodLabel != null && (
          <span
            className="text-sm text-text-muted"
            data-testid="district-population-period"
          >
            {periodLabel}
          </span>
        )}
      </CardHeader>
      <CardContent>
        <section data-testid="block-district-population">
          {total === null ? (
            <p
              data-testid="block-placeholder"
              className="text-sm lowercase text-text-muted"
            >
              {t('common.noData')}
            </p>
          ) : (
            <>
              <div data-testid="district-population-total">
                <span className="text-sm text-text-muted">
                  {t('digitalPassport.districtPopulation.total')}
                </span>
                <span className="mt-1 flex items-baseline gap-2">
                  <b className="text-3xl font-extrabold tabular-nums">
                    {fmt(total)}
                  </b>
                  <span className="text-sm text-text-muted">
                    {t('digitalPassport.districtPopulation.people')}
                  </span>
                </span>
              </div>

              {parts.length > 0 && (
                <ul className="mt-3.5 grid grid-cols-2 gap-3.5">
                  {parts.map(({ key, value, share: pct }) => (
                    <li
                      key={key}
                      data-testid={`district-population-${key}`}
                      className="rounded-2xl bg-surface-muted/60 px-5 py-4"
                    >
                      <span className="block text-sm text-text-muted">
                        {t(`digitalPassport.districtPopulation.${key}`)}
                      </span>
                      <span className="mt-1 flex flex-wrap items-baseline gap-x-2">
                        <b className="text-xl font-extrabold tabular-nums">
                          {fmt(value)}
                        </b>
                        {pct !== null && (
                          <span
                            className="text-sm tabular-nums text-brand-600"
                            data-testid={`district-population-${key}-share`}
                          >
                            {pct}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      </CardContent>
    </Card>
  );
};
